import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Stack,
  Box,
} from "@mui/material";
import {
  setMerchants,
  selectedMerchant as setSelectedMerchant,
} from "redux/actions";
import { useApi } from "hooks/useAPI";
import { Merchant } from "types/merchant";
import { useState, useEffect } from "react";
import VuiButton from "components/VuiButton";
import FormField from "./components/FormField";
import { useSnackbar } from "notistack";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiSelect from "components/VuiSelect";
import AppService from "services/AppService";

interface QuickbooksSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function QuickbooksSettingsModal({
  isOpen,
  onClose,
}: QuickbooksSettingsModalProps) {
  const { getToken } = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedAccounts, setSelectedAccounts] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [accounts, setAccounts] = useState<{ id: string; name: string }[]>([]);
  const dispatch = useDispatch();

  const merchants = useSelector(
    (state: any) => state.merchants.merchant as Merchant[]
  );
  const selectedMerchant = useSelector(
    (state: any) => state.merchants.selectedMerchant as Merchant
  );

  useEffect(() => {
    if (selectedMerchant) {
      setSelectedAccounts(selectedMerchant.quickbooksSelectedAccountIds || []);
    }
  }, [selectedMerchant]);

  useEffect(() => {
    const fetchQuickbooksAccounts = async () => {
      if (!selectedMerchant?.id) return;

      setLoading(true);
      const token = getToken ? await getToken() : undefined;
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await AppService.getQuickbooksAccounts({
          token,
          merchantId: selectedMerchant.id,
        });

        if (response?.data) {
          setAccounts(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch QuickBooks accounts:", error);
      }

      setLoading(false);
    };

    if (isOpen) {
      fetchQuickbooksAccounts();
    }
  }, [selectedMerchant, getToken, isOpen]);

  const onUpdate = async () => {
    const token = getToken ? await getToken() : undefined;
    if (!token) return;

    try {
      const updatedValues = {
        selectedAccountIds: selectedAccounts.map(acc => acc.id),
        merchantId: selectedMerchant.id,
      };

      const resp = await AppService.updateQuickbooksAccounts(updatedValues, token);

      if (resp?.data) {
        enqueueSnackbar("Updated Successfully!", { variant: "success" });

        const updatedMerchant = {
          ...selectedMerchant,
          quickbooksSelectedAccountIds: selectedAccounts
        };

        const updatedMerchants = merchants.map((m) =>
          m.id === selectedMerchant.id ? updatedMerchant : m
        );

        dispatch(setMerchants(updatedMerchants));
        dispatch(setSelectedMerchant(updatedMerchant));
        onClose();
      } else {
        enqueueSnackbar("Failed to update QuickBooks accounts", { variant: "error" });
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.data?.message || error?.message || 'Failed to update QuickBooks accounts';
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby="quickbooks-settings-dialog"
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.87);",
          color: "white",
          height: "500px",
        },
      }}
    >
      <DialogTitle
        id="quickbooks-settings-dialog"
        sx={{ color: "white !important" }}
      >
        Edit QuickBooks Details
      </DialogTitle>
      <DialogContent>
        <Grid item xs={12} sm={6}>
          <Stack direction="column" spacing={2}>
            <Box>
              <VuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <VuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  color="white"
                >
                  QuickBooks Accounts
                </VuiTypography>
              </VuiBox>
              <VuiSelect
                //@ts-ignore
                isMulti
                maxMenuHeight={200}
                value={selectedAccounts.map(acc => ({
                  value: acc.id,
                  label: acc.name
                }))}
                options={accounts.map(account => ({
                  value: account.id,
                  label: account.name
                }))}
                size="large"
                onChange={(newValue: { value: string; label: string }[]) => {
                  setSelectedAccounts(newValue.map(v => ({
                    id: v.value,
                    name: v.label
                  })));
                }}
                isClearable
                isLoading={loading}
              />
            </Box>
          </Stack>
        </Grid>
      </DialogContent>
      <DialogActions>
        {/* @ts-ignore */}
        <VuiButton variant="outlined" onClick={onClose}>
          Cancel
        </VuiButton>
        {/* @ts-ignore */}
        <VuiButton variant="contained" onClick={onUpdate}>
          Update
        </VuiButton>
      </DialogActions>
    </Dialog>
  );
} 